import React from 'react';
import axios from 'axios';
import {
    Alert,
    Button,
    CustomInput,
    FormGroup,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Table,
} from 'reactstrap';

import Widget from '../../../components/Widget/Widget';
import config from '../../../config';
import { formatApiDateTime } from '../../../utils/merchantLocalDateTime';

import s from './IntegrationsSettings.module.scss';
import { withTranslation } from '../../../i18n/LanguageContext';

const FILL_SHOPIFY_TO_BISTRO = 'ShopifyToBistro';
const FILL_BISTRO_TO_SHOPIFY = 'BistroToShopify';
const FILL_MATCH_ONLY = 'MatchOnly';
const SHOPIFY_HOME = 'https://www.shopify.com';
const SHOPIFY_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/assets/shopify-logo.svg`;
const SHOPIFY_BAG_SRC = `${process.env.PUBLIC_URL || ''}/assets/shopify-bag.svg`;
const CHECK_HOME = 'https://www.checkhq.com';
const CHECK_ONBOARD_SDK = 'https://cdn.checkhq.com/onboard-initialize.js';
const CHECK_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/assets/check-logo.svg`;

function apiBaseUrl() {
    const runtime = typeof window !== 'undefined' ? window.__BISTRO_API_BASE_URL__ : '';
    return String(runtime || config.baseURLApi || '').replace(/\/$/, '');
}

function shopifyOAuthRedirectUri(shopify) {
    const fromApi = shopify && (shopify.OAuthRedirectUri || shopify.oAuthRedirectUri || shopify.oauthRedirectUri);
    if (fromApi) {
        return fromApi;
    }
    const base = apiBaseUrl();
    return base ? `${base}/api/integrations/shopify/oauth/callback` : '';
}

function pick(obj, pascal, camel, fallback) {
    if (!obj) {
        return fallback;
    }
    if (obj[pascal] != null) {
        return obj[pascal];
    }
    if (obj[camel] != null) {
        return obj[camel];
    }
    return fallback;
}

function normalizeShopify(raw) {
    const src = raw || {};
    return {
        AppConfigured: !!pick(src, 'AppConfigured', 'appConfigured', false),
        HasClientSecret: !!pick(src, 'HasClientSecret', 'hasClientSecret', false),
        ClientId: pick(src, 'ClientId', 'clientId', '') || '',
        OAuthRedirectUri: pick(src, 'OAuthRedirectUri', 'oAuthRedirectUri', '')
            || src.oauthRedirectUri
            || '',
        Connected: !!pick(src, 'Connected', 'connected', false),
        SyncEnabled: !!pick(src, 'SyncEnabled', 'syncEnabled', false),
        ShopDomain: pick(src, 'ShopDomain', 'shopDomain', '') || '',
        LocationName: pick(src, 'LocationName', 'locationName', '') || '',
        ShopifyLocationId: pick(src, 'ShopifyLocationId', 'shopifyLocationId', '') || '',
        InitialSyncDirection: pick(src, 'InitialSyncDirection', 'initialSyncDirection', '') || '',
        LastError: pick(src, 'LastError', 'lastError', '') || '',
        LastReconcileAt: pick(src, 'LastReconcileAt', 'lastReconcileAt', null),
        MappedCount: pick(src, 'MappedCount', 'mappedCount', 0) || 0,
        OpenConflictCount: pick(src, 'OpenConflictCount', 'openConflictCount', 0) || 0,
        FailedOutboxCount: pick(src, 'FailedOutboxCount', 'failedOutboxCount', 0) || 0,
        CatalogSyncEnabled: !!pick(src, 'CatalogSyncEnabled', 'catalogSyncEnabled', false),
        CatalogWriteGranted: !!pick(src, 'CatalogWriteGranted', 'catalogWriteGranted', false),
        LastCatalogSyncAt: pick(src, 'LastCatalogSyncAt', 'lastCatalogSyncAt', null),
        ShopifyCurrencyCode: pick(src, 'ShopifyCurrencyCode', 'shopifyCurrencyCode', '') || '',
        AccountCurrencyCode: pick(src, 'AccountCurrencyCode', 'accountCurrencyCode', '') || '',
        CatalogFxRate: (() => {
            const raw = pick(src, 'CatalogFxRate', 'catalogFxRate', null);
            if (raw == null || raw === '') return null;
            const n = Number(raw);
            return Number.isFinite(n) ? n : null;
        })(),
        FxRequired: !!pick(src, 'FxRequired', 'fxRequired', false),
        FxPriceOpsBlocked: !!pick(src, 'FxPriceOpsBlocked', 'fxPriceOpsBlocked', false),
        FxNote: pick(src, 'FxNote', 'fxNote', '') || '',
    };
}

function isFxPriceBlocked(shopify) {
    if (!shopify || !shopify.Connected) return false;
    // Server flag covers unknown shop currency OR required rate missing.
    if (shopify.FxPriceOpsBlocked) return true;
    if (!shopify.ShopifyCurrencyCode) return true;
    if (!shopify.FxRequired) return false;
    const rate = Number(shopify.CatalogFxRate);
    return !(Number.isFinite(rate) && rate > 0);
}

function rowKey(row) {
    return `${pick(row, 'EntityType', 'entityType', '')}-${pick(row, 'EntityId', 'entityId', '')}`;
}

function variantOptionLabel(variant) {
    const title = pick(variant, 'Title', 'title', '') || 'Untitled';
    const sku = pick(variant, 'Sku', 'sku', '');
    const qty = pick(variant, 'Available', 'available', null);
    const qtyPart = qty == null ? '' : ` · qty ${qty}`;
    return sku ? `${title} (${sku})${qtyPart}` : `${title}${qtyPart}`;
}

function isSafeAuthorizeUrl(url) {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:'
            && parsed.hostname.toLowerCase().endsWith('.myshopify.com')
            && parsed.pathname.indexOf('/admin/oauth/authorize') === 0;
    } catch (err) {
        return false;
    }
}

function isSafeCheckOnboardUrl(url) {
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();
        return parsed.protocol === 'https:'
            && (host === 'checkhq.com' || host.endsWith('.checkhq.com'));
    } catch (err) {
        return false;
    }
}

function loadCheckOnboardSdk() {
    return new Promise((resolve, reject) => {
        if (window.Check && typeof window.Check.create === 'function') {
            resolve(window.Check);
            return;
        }
        const existing = document.querySelector('script[data-check-onboard]');
        if (existing) {
            existing.addEventListener('load', () => resolve(window.Check));
            existing.addEventListener('error', () => reject(new Error('Unable to load Check Onboard.')));
            return;
        }
        const script = document.createElement('script');
        script.src = CHECK_ONBOARD_SDK;
        script.async = true;
        script.setAttribute('data-check-onboard', 'true');
        script.onload = () => resolve(window.Check);
        script.onerror = () => reject(new Error('Unable to load Check Onboard.'));
        document.head.appendChild(script);
    });
}

function normalizeCheck(raw) {
    const src = raw || {};
    const prefill = pick(src, 'Prefill', 'prefill', null) || {};
    return {
        AppConfigured: !!pick(src, 'AppConfigured', 'appConfigured', false),
        IsSandbox: !!pick(src, 'IsSandbox', 'isSandbox', false),
        Connected: !!pick(src, 'Connected', 'connected', false),
        PayrollEnabled: !!pick(src, 'PayrollEnabled', 'payrollEnabled', false),
        CompanyId: pick(src, 'CompanyId', 'companyId', '') || '',
        WorkplaceId: pick(src, 'WorkplaceId', 'workplaceId', '') || '',
        LegalName: pick(src, 'LegalName', 'legalName', '') || '',
        TradeName: pick(src, 'TradeName', 'tradeName', '') || '',
        OnboardStatus: pick(src, 'OnboardStatus', 'onboardStatus', '') || '',
        BlockingSteps: pick(src, 'BlockingSteps', 'blockingSteps', []) || [],
        RemainingSteps: pick(src, 'RemainingSteps', 'remainingSteps', []) || [],
        InGoodStanding: !!pick(src, 'InGoodStanding', 'inGoodStanding', false),
        CanEnablePayroll: !!pick(src, 'CanEnablePayroll', 'canEnablePayroll', false),
        LastError: pick(src, 'LastError', 'lastError', '') || '',
        MappedEmployeeCount: pick(src, 'MappedEmployeeCount', 'mappedEmployeeCount', 0) || 0,
        WebhookConfigured: !!pick(src, 'WebhookConfigured', 'webhookConfigured', false),
        Prefill: {
            LegalName: pick(prefill, 'LegalName', 'legalName', '') || '',
            TradeName: pick(prefill, 'TradeName', 'tradeName', '') || '',
            AddressLine1: pick(prefill, 'AddressLine1', 'addressLine1', '') || '',
            AddressLine2: pick(prefill, 'AddressLine2', 'addressLine2', '') || '',
            City: pick(prefill, 'City', 'city', '') || '',
            State: pick(prefill, 'State', 'state', '') || '',
            PostalCode: pick(prefill, 'PostalCode', 'postalCode', '') || '',
            Email: pick(prefill, 'Email', 'email', '') || '',
            Phone: pick(prefill, 'Phone', 'phone', '') || '',
            StartDate: pick(prefill, 'StartDate', 'startDate', '') || '',
            PayFrequency: pick(prefill, 'PayFrequency', 'payFrequency', 'biweekly') || 'biweekly',
            SignerName: pick(prefill, 'SignerName', 'signerName', '') || '',
            SignerTitle: pick(prefill, 'SignerTitle', 'signerTitle', '') || 'Owner',
            SignerEmail: pick(prefill, 'SignerEmail', 'signerEmail', '') || '',
        },
    };
}

function checkOnboardLabel(status) {
    if (!status) {
        return 'Onboard not started';
    }
    if (status === 'completed') {
        return 'Onboard complete';
    }
    if (status === 'blocking') {
        return 'Onboard incomplete';
    }
    if (status === 'needs_attention') {
        return 'Onboard needs attention';
    }
    return `Onboard: ${status}`;
}

class IntegrationsSettings extends React.Component {
    state = {
        loading: true,
        saving: false,
        error: '',
        notice: '',
        shopify: null,
        shopDomain: '',
        clientId: '',
        clientSecret: '',
        panelOpen: false,
        helpOpen: false,
        helpPos: { x: 72, y: 72 },
        locations: [],
        locationId: '',
        preview: null,
        importPreview: null,
        importPreviewError: '',
        importCategoryId: '',
        importScope: 'All',
        fxRateDraft: '',
        savingFxRate: false,
        unmatchedOpen: false,
        fillDirection: FILL_SHOPIFY_TO_BISTRO,
        conflicts: [],
        linkByKey: {},
        linkingKey: '',
        resolvingId: null,
        check: null,
        checkLoading: true,
        checkSaving: false,
        checkError: '',
        checkNotice: '',
        checkPanelOpen: false,
        checkLegalName: '',
        checkTradeName: '',
        checkAddressLine1: '',
        checkAddressLine2: '',
        checkCity: '',
        checkState: '',
        checkPostalCode: '',
        checkEmail: '',
        checkPhone: '',
        checkStartDate: '',
        checkPayFrequency: 'biweekly',
        checkSignerName: '',
        checkSignerTitle: 'Owner',
        checkSignerEmail: '',
        checkEmployees: [],
        checkEmployeeBusyId: null,
        checkNameUser: null,
        checkLegalFirst: '',
        checkLegalLast: '',
    };

    checkOnboardHandler = null;
    helpDrag = null;

    componentDidMount() {
        const params = new URLSearchParams(window.location.search || '');
        const shopifyResult = params.get('shopify');
        const message = params.get('message');
        if (window.history && window.history.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname + (window.location.hash || ''));
        }
        this.loadStatus().then(() => {
            if (shopifyResult === 'connected') {
                this.setState({
                    notice: 'Shopify connected. Select a location, review matches, then choose how to copy stock once.',
                    panelOpen: false,
                    error: '',
                });
            } else if (shopifyResult === 'error') {
                this.setState({
                    error: message || 'Unable to complete Shopify connect.',
                    panelOpen: true,
                    notice: '',
                });
            }
        });
        this.loadCheckStatus();
    }

    componentWillUnmount() {
        this.closeCheckOnboardHandler();
        this.endHelpDrag();
    }

    closeCheckOnboardHandler = () => {
        if (this.checkOnboardHandler && typeof this.checkOnboardHandler.close === 'function') {
            try {
                this.checkOnboardHandler.close();
            } catch (err) {
                // Check SDK close is best-effort.
            }
        }
        this.checkOnboardHandler = null;
    };

    openPanel = () => {
        const shopify = this.state.shopify || {};
        this.setState({
            panelOpen: true,
            error: '',
            clientId: shopify.ClientId || this.state.clientId,
            clientSecret: '',
            shopDomain: shopify.ShopDomain || this.state.shopDomain,
        });
    };

    closePanel = () => {
        this.endHelpDrag();
        this.setState({ panelOpen: false, clientSecret: '', helpOpen: false });
    };

    openShopifyHelp = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const width = 400;
        const left = typeof window !== 'undefined'
            ? Math.max(16, Math.min((window.innerWidth || 800) - width - 24, (window.innerWidth || 800) * 0.55))
            : 72;
        this.setState({
            helpOpen: true,
            helpPos: { x: left, y: 80 },
        });
    };

    closeShopifyHelp = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.endHelpDrag();
        this.setState({ helpOpen: false });
    };

    startHelpDrag = (event) => {
        if (event.button != null && event.button !== 0) {
            return;
        }
        if (event.target && event.target.closest && event.target.closest('button')) {
            return;
        }
        event.preventDefault();
        const point = event.touches ? event.touches[0] : event;
        this.helpDrag = {
            startX: point.clientX,
            startY: point.clientY,
            origX: this.state.helpPos.x,
            origY: this.state.helpPos.y,
        };
        window.addEventListener('mousemove', this.moveHelpDrag);
        window.addEventListener('mouseup', this.endHelpDrag);
        window.addEventListener('touchmove', this.moveHelpDrag, { passive: false });
        window.addEventListener('touchend', this.endHelpDrag);
    };

    moveHelpDrag = (event) => {
        if (!this.helpDrag) {
            return;
        }
        if (event.cancelable) {
            event.preventDefault();
        }
        const point = event.touches ? event.touches[0] : event;
        const width = 400;
        const maxX = typeof window !== 'undefined' ? Math.max(16, (window.innerWidth || 800) - width - 16) : 800;
        const maxY = typeof window !== 'undefined' ? Math.max(16, (window.innerHeight || 600) - 80) : 600;
        const x = Math.min(maxX, Math.max(16, this.helpDrag.origX + (point.clientX - this.helpDrag.startX)));
        const y = Math.min(maxY, Math.max(16, this.helpDrag.origY + (point.clientY - this.helpDrag.startY)));
        this.setState({ helpPos: { x, y } });
    };

    endHelpDrag = () => {
        this.helpDrag = null;
        window.removeEventListener('mousemove', this.moveHelpDrag);
        window.removeEventListener('mouseup', this.endHelpDrag);
        window.removeEventListener('touchmove', this.moveHelpDrag);
        window.removeEventListener('touchend', this.endHelpDrag);
    };

    loadStatus = async () => {
        this.setState({ loading: true, error: '' });
        try {
            const res = await axios.get('/api/integrations/shopify/status');
            if (res.data.status !== 'Ok') {
                this.setState({ loading: false, error: res.data.message || 'Unable to load Shopify status.' });
                return;
            }
            const shopify = normalizeShopify(res.data.shopify);
            this.setState({
                loading: false,
                shopify,
                shopDomain: shopify.ShopDomain || this.state.shopDomain,
                clientId: shopify.ClientId || this.state.clientId,
                locationId: shopify.ShopifyLocationId || '',
                fillDirection: shopify.InitialSyncDirection || FILL_SHOPIFY_TO_BISTRO,
                fxRateDraft: shopify.CatalogFxRate != null ? String(shopify.CatalogFxRate) : '',
            });
            if (shopify.Connected) {
                await this.loadLocationsAndMatch(shopify.ShopifyLocationId || '');
                await this.loadConflicts();
            } else {
                this.setState({ conflicts: [], linkByKey: {}, importPreview: null });
            }
        } catch (err) {
            this.setState({ loading: false, error: (err.response && err.response.data && err.response.data.message) || err.message || 'Unable to load Shopify status.' });
        }
    };

    loadLocationsAndMatch = async (currentLocationId) => {
        try {
            const res = await axios.get('/api/integrations/shopify/locations');
            if (res.data.status !== 'Ok') {
                this.setState({ error: res.data.message || 'Unable to load Shopify locations.' });
                return;
            }
            const locations = res.data.locations || [];
            let locationId = currentLocationId || this.state.locationId;
            const active = locations.filter((loc) => loc.IsActive !== false);
            if (!locationId && active.length === 1) {
                locationId = active[0].Id;
            }
            this.setState({ locations, locationId }, () => {
                this.loadMatch();
                this.loadImportPreview();
            });
        } catch (err) {
            this.setState({ error: err.message || 'Unable to load Shopify locations.' });
        }
    };

    loadMatch = async () => {
        try {
            const locationId = this.state.locationId;
            const res = await axios.get('/api/integrations/shopify/match', {
                params: locationId ? { locationId } : {},
            });
            if (res.data.status === 'Ok') {
                this.setState({ preview: res.data.preview || null });
            } else {
                this.setState({ error: res.data.message || 'Unable to match catalog SKUs.' });
            }
        } catch (err) {
            this.setState({ error: err.message || 'Unable to match catalog SKUs.' });
        }
    };

    loadImportPreview = async () => {
        try {
            const { locationId, importScope } = this.state;
            const scope = importScope === 'Location' ? 'Location' : 'All';
            if (scope === 'Location' && !locationId) {
                this.setState({
                    importPreview: null,
                    importPreviewError: 'Choose a Shopify location for store-scoped import.',
                });
                return null;
            }
            const params = { importScope: scope };
            if (locationId) {
                params.locationId = locationId;
            }
            const res = await axios.get('/api/integrations/shopify/catalog/import-preview', { params });
            if (res.data.status === 'Ok') {
                const preview = res.data.preview || null;
                const suggested = preview && (preview.SuggestedCategoryId || preview.suggestedCategoryId);
                this.setState({
                    importPreview: preview,
                    importPreviewError: '',
                    importCategoryId: this.state.importCategoryId || (suggested ? String(suggested) : ''),
                });
                try {
                    const statusRes = await axios.get('/api/integrations/shopify/status');
                    if (statusRes.data.status === 'Ok') {
                        const shopify = normalizeShopify(statusRes.data.shopify);
                        this.setState({
                            shopify,
                            fxRateDraft: shopify.CatalogFxRate != null
                                ? String(shopify.CatalogFxRate)
                                : this.state.fxRateDraft,
                        });
                    }
                } catch (statusErr) {
                    // Preview succeeded; currency refresh is best-effort.
                }
                return preview;
            }
            this.setState({
                importPreview: null,
                importPreviewError: res.data.message || 'Unable to preview Shopify products to import.',
            });
            return null;
        } catch (err) {
            this.setState({
                importPreview: null,
                importPreviewError: err.message || 'Unable to preview Shopify products to import.',
            });
            return null;
        }
    };

    loadConflicts = async () => {
        try {
            const res = await axios.get('/api/integrations/shopify/conflicts');
            if (res.data.status === 'Ok') {
                this.setState({ conflicts: res.data.conflicts || [] });
            }
        } catch (err) {
            this.setState({ conflicts: [] });
        }
    };

    canConnect = () => {
        const { shopDomain, clientId, clientSecret, shopify, saving } = this.state;
        const hasSecret = !!(clientSecret && clientSecret.trim()) || !!(shopify && shopify.HasClientSecret);
        const hasId = !!(clientId && clientId.trim()) || !!(shopify && shopify.AppConfigured);
        return !saving && !!(shopDomain && shopDomain.trim()) && hasId && (hasSecret || !!(shopify && shopify.AppConfigured));
    };

    saveCredentials = async () => {
        const clientId = (this.state.clientId || '').trim();
        const shopDomain = (this.state.shopDomain || '').trim();
        const clientSecret = (this.state.clientSecret || '').trim();
        if (!clientId || !shopDomain || (!clientSecret && !(this.state.shopify && this.state.shopify.HasClientSecret))) {
            this.setState({ error: 'Enter Client ID, Client secret, and store domain.' });
            return;
        }
        this.setState({ saving: true, error: '', notice: '' });
        try {
            const res = await axios.put('/api/integrations/shopify/credentials', {
                ShopDomain: shopDomain,
                ClientId: clientId,
                ClientSecret: clientSecret || undefined,
            });
            if (res.data.status !== 'Ok') {
                this.setState({ saving: false, error: res.data.message || 'Unable to save Shopify credentials.' });
                return;
            }
            this.setState({
                saving: false,
                shopify: normalizeShopify(res.data.shopify || this.state.shopify),
                clientSecret: '',
                notice: 'Shopify app credentials saved.',
            });
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Unable to save Shopify credentials.' });
        }
    };

    connect = async () => {
        if (!this.canConnect()) {
            this.setState({ error: 'Enter Client ID, Client secret, and store domain.' });
            return;
        }
        this.setState({ saving: true, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/connect-url', {
                ShopDomain: (this.state.shopDomain || '').trim(),
                ClientId: (this.state.clientId || '').trim() || undefined,
                ClientSecret: (this.state.clientSecret || '').trim() || undefined,
            });
            if (res.data.status !== 'Ok') {
                this.setState({ saving: false, error: res.data.message || 'Unable to start Shopify connect.' });
                return;
            }
            const authorizeUrl = res.data.authorizeUrl || res.data.AuthorizeUrl;
            if (!isSafeAuthorizeUrl(authorizeUrl)) {
                this.setState({ saving: false, error: 'Shopify returned an invalid connect URL.' });
                return;
            }
            window.location.href = authorizeUrl;
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Unable to start Shopify connect.' });
        }
    };

    copyRedirect = async () => {
        const uri = shopifyOAuthRedirectUri(this.state.shopify);
        if (!uri) {
            this.setState({ error: 'Redirect URL is not available.' });
            return;
        }
        try {
            await navigator.clipboard.writeText(uri);
            this.setState({ notice: 'Redirect URL copied. Paste it into your Shopify app as an Allowed redirection URL.' });
        } catch (err) {
            this.setState({ error: 'Unable to copy the redirect URL.' });
        }
    };

    applyCheckPrefill = (check) => {
        const prefill = (check && check.Prefill) || {};
        this.setState({
            checkLegalName: this.state.checkLegalName || prefill.LegalName || '',
            checkTradeName: this.state.checkTradeName || prefill.TradeName || '',
            checkAddressLine1: this.state.checkAddressLine1 || prefill.AddressLine1 || '',
            checkAddressLine2: this.state.checkAddressLine2 || prefill.AddressLine2 || '',
            checkCity: this.state.checkCity || prefill.City || '',
            checkState: this.state.checkState || prefill.State || '',
            checkPostalCode: this.state.checkPostalCode || prefill.PostalCode || '',
            checkEmail: this.state.checkEmail || prefill.Email || '',
            checkPhone: this.state.checkPhone || prefill.Phone || '',
            checkStartDate: this.state.checkStartDate || prefill.StartDate || '',
            checkPayFrequency: this.state.checkPayFrequency || prefill.PayFrequency || 'biweekly',
            checkSignerName: this.state.checkSignerName || prefill.SignerName || '',
            checkSignerTitle: this.state.checkSignerTitle || prefill.SignerTitle || 'Owner',
            checkSignerEmail: this.state.checkSignerEmail || prefill.SignerEmail || '',
        });
    };

    loadCheckStatus = async (options) => {
        const keepNotice = !!(options && options.keepNotice);
        this.setState({ checkLoading: true, checkError: '' });
        try {
            const res = await axios.get('/api/integrations/check/status');
            if (res.data.status !== 'Ok') {
                this.setState({ checkLoading: false, checkError: res.data.message || 'Unable to load Check status.' });
                return;
            }
            const check = normalizeCheck(res.data.check);
            this.setState({
                checkLoading: false,
                check,
                checkNotice: keepNotice ? this.state.checkNotice : '',
                checkEmployees: check.Connected ? this.state.checkEmployees : [],
            }, () => {
                this.applyCheckPrefill(check);
                if (check.Connected) {
                    this.loadCheckEmployees();
                }
            });
        } catch (err) {
            this.setState({
                checkLoading: false,
                checkError: (err.response && err.response.data && err.response.data.message) || err.message || 'Unable to load Check status.',
            });
        }
    };

    openCheckPanel = () => {
        const check = this.state.check || {};
        this.applyCheckPrefill(check);
        this.setState({ checkPanelOpen: true, checkError: '' });
    };

    closeCheckPanel = () => {
        this.setState({ checkPanelOpen: false });
    };

    checkConnectPayload = () => ({
        LegalName: (this.state.checkLegalName || '').trim(),
        TradeName: (this.state.checkTradeName || '').trim(),
        AddressLine1: (this.state.checkAddressLine1 || '').trim(),
        AddressLine2: (this.state.checkAddressLine2 || '').trim(),
        City: (this.state.checkCity || '').trim(),
        State: (this.state.checkState || '').trim(),
        PostalCode: (this.state.checkPostalCode || '').trim(),
        Email: (this.state.checkEmail || '').trim(),
        Phone: (this.state.checkPhone || '').trim(),
        StartDate: (this.state.checkStartDate || '').trim(),
        PayFrequency: (this.state.checkPayFrequency || 'biweekly').trim(),
        SignerName: (this.state.checkSignerName || '').trim(),
        SignerTitle: (this.state.checkSignerTitle || '').trim(),
        SignerEmail: (this.state.checkSignerEmail || '').trim(),
    });

    canConnectCheck = () => {
        const { check, checkSaving } = this.state;
        const payload = this.checkConnectPayload();
        return !checkSaving
            && !!(check && check.AppConfigured)
            && !!payload.LegalName
            && !!payload.AddressLine1
            && !!payload.City
            && !!payload.State
            && !!payload.PostalCode
            && !!payload.StartDate
            && !!payload.SignerName
            && !!payload.SignerTitle
            && !!payload.SignerEmail;
    };

    openCheckOnboard = async (url) => {
        if (!isSafeCheckOnboardUrl(url)) {
            this.setState({ checkSaving: false, checkError: 'Check returned an invalid Onboard URL.' });
            return;
        }
        try {
            this.closeCheckOnboardHandler();
            const CheckSdk = await loadCheckOnboardSdk();
            if (CheckSdk && typeof CheckSdk.create === 'function') {
                const handler = CheckSdk.create({
                    link: url,
                    onClose: () => {
                        this.closeCheckOnboardHandler();
                        this.setState({ checkSaving: false, checkPanelOpen: false, checkEmployeeBusyId: null });
                        this.loadCheckStatus({ keepNotice: true });
                    },
                    onEvent: (name) => {
                        if (name === 'check-onboard-app-completed') {
                            this.closeCheckOnboardHandler();
                            this.setState({
                                checkNotice: 'Check Onboard finished. Bank account, EIN, and filing authorization are stored at Check.',
                                checkPanelOpen: false,
                                checkError: '',
                            });
                            this.loadCheckStatus({ keepNotice: true });
                        }
                    },
                });
                this.checkOnboardHandler = handler;
                handler.open();
                this.setState({ checkSaving: false, checkPanelOpen: false });
                return;
            }
        } catch (err) {
            // Fall through to Check's documented redirect.
        }
        window.location.href = url;
    };

    connectCheck = async () => {
        if (!this.canConnectCheck()) {
            this.setState({ checkError: 'Enter the legal company address and the authorized signer, then connect.' });
            return;
        }
        this.setState({ checkSaving: true, checkError: '', checkNotice: '' });
        try {
            const connected = !!(this.state.check && this.state.check.Connected);
            const res = await axios.post(
                connected ? '/api/integrations/check/onboard-url' : '/api/integrations/check/connect',
                this.checkConnectPayload(),
            );
            if (res.data.status !== 'Ok') {
                this.setState({ checkSaving: false, checkError: res.data.message || 'Unable to start Check connect.' });
                return;
            }
            const onboardUrl = res.data.onboardUrl || res.data.OnboardUrl;
            if (res.data.check) {
                this.setState({ check: normalizeCheck(res.data.check) });
            }
            await this.openCheckOnboard(onboardUrl);
        } catch (err) {
            this.setState({ checkSaving: false, checkError: err.message || 'Unable to start Check connect.' });
        }
    };

    continueCheckOnboard = async () => {
        this.setState({ checkSaving: true, checkError: '', checkNotice: '' });
        try {
            const res = await axios.post('/api/integrations/check/onboard-url', this.checkConnectPayload());
            if (res.data.status !== 'Ok') {
                this.setState({ checkSaving: false, checkError: res.data.message || 'Unable to generate a Check Onboard link.' });
                return;
            }
            await this.openCheckOnboard(res.data.onboardUrl || res.data.OnboardUrl);
        } catch (err) {
            this.setState({ checkSaving: false, checkError: err.message || 'Unable to generate a Check Onboard link.' });
        }
    };

    enableCheckPayroll = async () => {
        this.setState({ checkSaving: true, checkError: '', checkNotice: '' });
        try {
            const res = await axios.post('/api/integrations/check/enable');
            if (res.data.status !== 'Ok') {
                this.setState({ checkSaving: false, checkError: res.data.message || 'Unable to enable Check payroll.' });
                return;
            }
            this.setState({
                checkSaving: false,
                check: normalizeCheck(res.data.check),
                checkNotice: 'Check payroll enabled for this location.',
            });
        } catch (err) {
            this.setState({ checkSaving: false, checkError: err.message || 'Unable to enable Check payroll.' });
        }
    };

    disableCheckPayroll = async () => {
        this.setState({ checkSaving: true, checkError: '', checkNotice: '' });
        try {
            const res = await axios.post('/api/integrations/check/disable');
            if (res.data.status !== 'Ok') {
                this.setState({ checkSaving: false, checkError: res.data.message || 'Unable to pause Check payroll.' });
                return;
            }
            this.setState({
                checkSaving: false,
                check: normalizeCheck(res.data.check),
                checkNotice: 'Check payroll paused. Wage ACH is off. Rebelity payroll settings stay on; floor sales and clock-in are unchanged.',
            });
        } catch (err) {
            this.setState({ checkSaving: false, checkError: err.message || 'Unable to pause Check payroll.' });
        }
    };

    loadCheckEmployees = async () => {
        try {
            const res = await axios.get('/api/integrations/check/employees');
            if (res.data.status !== 'Ok') {
                this.setState({
                    checkError: res.data.message || 'Unable to load Check employees.',
                });
                return;
            }
            this.setState({ checkEmployees: res.data.employees || res.data.Employees || [] });
        } catch (err) {
            this.setState({
                checkError: (err.response && err.response.data && err.response.data.message) || err.message || 'Unable to load Check employees.',
            });
        }
    };

    onboardCheckEmployee = async (row) => {
        const userId = pick(row, 'BistroUserId', 'bistroUserId', 0);
        if (!userId) {
            return;
        }
        const mapped = !!pick(row, 'Mapped', 'mapped', false);
        if (!mapped) {
            this.setState({
                checkNameUser: row,
                checkLegalFirst: '',
                checkLegalLast: '',
                checkError: '',
            });
            return;
        }
        await this.startCheckEmployeeOnboard(row, null, null);
    };

    submitCheckEmployeeName = async () => {
        const row = this.state.checkNameUser;
        const first = (this.state.checkLegalFirst || '').trim();
        const last = (this.state.checkLegalLast || '').trim();
        if (!row || !first || !last) {
            this.setState({ checkError: 'Enter the employee\'s legal first and last name as they should appear on a W-2.' });
            return;
        }
        this.setState({ checkNameUser: null });
        await this.startCheckEmployeeOnboard(row, first, last);
    };

    startCheckEmployeeOnboard = async (row, firstName, lastName) => {
        const userId = pick(row, 'BistroUserId', 'bistroUserId', 0);
        if (!userId) {
            return;
        }
        this.setState({ checkEmployeeBusyId: userId, checkError: '', checkNotice: '' });
        try {
            const payload = { BistroUserId: userId };
            if (firstName && lastName) {
                payload.FirstName = firstName;
                payload.LastName = lastName;
            }
            const res = await axios.post('/api/integrations/check/employees/onboard', payload);
            if (res.data.status !== 'Ok') {
                this.setState({
                    checkEmployeeBusyId: null,
                    checkError: res.data.message || 'Unable to start Check employee Onboard.',
                });
                return;
            }
            const employee = res.data.employee || res.data.Employee;
            if (employee) {
                this.setState((prev) => ({
                    checkEmployees: (prev.checkEmployees || []).map((item) => (
                        pick(item, 'BistroUserId', 'bistroUserId', 0) === userId ? { ...item, ...employee } : item
                    )),
                }));
            }
            await this.openCheckOnboard(res.data.onboardUrl || res.data.OnboardUrl);
            this.setState({ checkEmployeeBusyId: null });
            this.loadCheckEmployees();
        } catch (err) {
            this.setState({
                checkEmployeeBusyId: null,
                checkError: err.message || 'Unable to start Check employee Onboard.',
            });
        }
    };

    disconnectCheck = async () => {
        if (!window.confirm('Disconnect Check for this location? The Check company is kept so you can resume Onboard later.')) {
            return;
        }
        this.setState({ checkSaving: true, checkError: '', checkNotice: '' });
        try {
            const res = await axios.post('/api/integrations/check/disconnect');
            if (res.data.status !== 'Ok') {
                this.setState({ checkSaving: false, checkError: res.data.message || 'Unable to disconnect Check.' });
                return;
            }
            this.setState({
                checkSaving: false,
                check: normalizeCheck(res.data.check),
                checkNotice: 'Check disconnected for this location.',
                checkEmployees: [],
            });
        } catch (err) {
            this.setState({ checkSaving: false, checkError: err.message || 'Unable to disconnect Check.' });
        }
    };

    renderCheckFields() {
        const { checkSaving } = this.state;
        return (
            <div>
                <p className={s.noteText}>
                    Rebelity creates a Check company with your legal name and address, then opens Check Onboard
                    for the authorized signer. Check collects the business bank account (Plaid), EIN, state tax IDs,
                    and Form 8655. Rebelity never asks for a Check API key here.
                </p>
                <FormGroup>
                    <label htmlFor="checkLegalName">Legal name</label>
                    <Input
                        id="checkLegalName"
                        value={this.state.checkLegalName}
                        onChange={(e) => this.setState({ checkLegalName: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkTradeName">Trade name</label>
                    <Input
                        id="checkTradeName"
                        value={this.state.checkTradeName}
                        onChange={(e) => this.setState({ checkTradeName: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkAddressLine1">Legal address</label>
                    <Input
                        id="checkAddressLine1"
                        value={this.state.checkAddressLine1}
                        placeholder="Street"
                        onChange={(e) => this.setState({ checkAddressLine1: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkAddressLine2">Address line 2</label>
                    <Input
                        id="checkAddressLine2"
                        value={this.state.checkAddressLine2}
                        placeholder="Suite, unit"
                        onChange={(e) => this.setState({ checkAddressLine2: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkCity">City</label>
                    <Input
                        id="checkCity"
                        value={this.state.checkCity}
                        onChange={(e) => this.setState({ checkCity: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkState">State</label>
                    <Input
                        id="checkState"
                        value={this.state.checkState}
                        placeholder="GA"
                        maxLength={2}
                        onChange={(e) => this.setState({ checkState: e.target.value.toUpperCase() })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkPostalCode">ZIP</label>
                    <Input
                        id="checkPostalCode"
                        value={this.state.checkPostalCode}
                        onChange={(e) => this.setState({ checkPostalCode: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkStartDate">First payday with Check</label>
                    <Input
                        id="checkStartDate"
                        type="date"
                        value={this.state.checkStartDate}
                        onChange={(e) => this.setState({ checkStartDate: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkPayFrequency">Pay frequency</label>
                    <Input
                        id="checkPayFrequency"
                        type="select"
                        value={this.state.checkPayFrequency}
                        onChange={(e) => this.setState({ checkPayFrequency: e.target.value })}
                        disabled={checkSaving}
                    >
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Biweekly</option>
                        <option value="semimonthly">Semimonthly</option>
                        <option value="monthly">Monthly</option>
                    </Input>
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkEmail">Payroll email</label>
                    <Input
                        id="checkEmail"
                        type="email"
                        value={this.state.checkEmail}
                        onChange={(e) => this.setState({ checkEmail: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkPhone">Phone</label>
                    <Input
                        id="checkPhone"
                        value={this.state.checkPhone}
                        onChange={(e) => this.setState({ checkPhone: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkSignerName">Authorized signer name</label>
                    <Input
                        id="checkSignerName"
                        value={this.state.checkSignerName}
                        onChange={(e) => this.setState({ checkSignerName: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkSignerTitle">Signer title</label>
                    <Input
                        id="checkSignerTitle"
                        value={this.state.checkSignerTitle}
                        placeholder="Owner, Officer, Manager"
                        onChange={(e) => this.setState({ checkSignerTitle: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="checkSignerEmail">Signer email</label>
                    <Input
                        id="checkSignerEmail"
                        type="email"
                        value={this.state.checkSignerEmail}
                        onChange={(e) => this.setState({ checkSignerEmail: e.target.value })}
                        disabled={checkSaving}
                    />
                </FormGroup>
            </div>
        );
    }

    disconnect = async () => {
        if (!window.confirm(this.props.t('integrations.disconnectConfirm'))) {
            return;
        }
        this.setState({ saving: true, error: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/disconnect');
            if (res.data.status !== 'Ok') {
                this.setState({ saving: false, error: res.data.message || 'Unable to disconnect.' });
                return;
            }
            this.setState({ saving: false, preview: null, locations: [], notice: 'Shopify disconnected.', panelOpen: true });
            this.loadStatus();
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Unable to disconnect.' });
        }
    };

    enable = async () => {
        const { locations, locationId, preview } = this.state;
        if (locations.length > 1 && !locationId) {
            this.setState({ error: 'Select a Shopify location before enabling sync.' });
            return;
        }
        const matched = (preview && (preview.Matches || preview.matches)) ? (preview.Matches || preview.matches).length : 0;
        if (matched === 0 && !window.confirm('No SKUs matched. Enable sync anyway? Unmatched items will not sync until they share a SKU or you link them to a Shopify variant.')) {
            return;
        }
        this.setState({ saving: true, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/enable', {
                LocationId: this.state.locationId,
                InitialSyncDirection: this.state.fillDirection,
                PersistComposedSkus: true,
            });
            if (res.data.status !== 'Ok') {
                this.setState({ saving: false, error: res.data.message || 'Unable to enable inventory sync.' });
                return;
            }
            this.setState({ saving: false, notice: 'Inventory sync enabled.' });
            await this.loadStatus();
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Unable to enable inventory sync.' });
        }
    };

    disable = async () => {
        this.setState({ saving: true, error: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/disable');
            if (res.data.status !== 'Ok') {
                this.setState({ saving: false, error: res.data.message || 'Unable to pause sync.' });
                return;
            }
            this.setState({ saving: false, notice: 'Inventory sync paused.' });
            this.loadStatus();
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Unable to pause sync.' });
        }
    };

    pull = async () => {
        this.setState({ saving: true, error: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/pull');
            this.setState({
                saving: false,
                error: res.data.status === 'Ok' ? '' : (res.data.message || 'Pull failed.'),
                notice: res.data.status === 'Ok' ? `Copied ${res.data.result.UpdatedCount} quantities from the online store.` : '',
            });
            if (res.data.status === 'Ok') {
                await this.loadConflicts();
                await this.loadMatch();
            }
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Pull failed.' });
        }
    };

    push = async () => {
        this.setState({ saving: true, error: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/push');
            this.setState({
                saving: false,
                error: res.data.status === 'Ok' ? '' : (res.data.message || 'Push failed.'),
                notice: res.data.status === 'Ok' ? `Copied ${res.data.result.UpdatedCount} quantities to the online store.` : '',
            });
            if (res.data.status === 'Ok') {
                await this.loadConflicts();
                await this.loadMatch();
            }
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Push failed.' });
        }
    };

    saveFxRate = async () => {
        const { fxRateDraft, savingFxRate, shopify } = this.state;
        if (savingFxRate) return;
        const trimmed = (fxRateDraft || '').trim();
        let rate = null;
        if (trimmed !== '') {
            rate = Number(trimmed);
            if (!Number.isFinite(rate) || rate <= 0) {
                this.setState({ error: this.props.t('integrations.fxRateInvalid') });
                return;
            }
        } else if (shopify && shopify.FxRequired) {
            this.setState({ error: this.props.t('integrations.fxRateRequired') });
            return;
        }
        this.setState({ savingFxRate: true, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/catalog/fx-rate', { Rate: rate });
            if (res.data.status !== 'Ok') {
                this.setState({
                    savingFxRate: false,
                    error: res.data.message || this.props.t('integrations.fxRateSaveFailed'),
                });
                return;
            }
            const next = normalizeShopify(res.data.shopify || this.state.shopify);
            this.setState({
                savingFxRate: false,
                shopify: next,
                fxRateDraft: next.CatalogFxRate != null ? String(next.CatalogFxRate) : '',
                notice: this.props.t('integrations.fxRateSaved'),
            });
        } catch (err) {
            this.setState({
                savingFxRate: false,
                error: err.message || this.props.t('integrations.fxRateSaveFailed'),
            });
        }
    };

    importCatalog = async () => {
        const preview = this.state.importPreview || {};
        const createCount = pick(preview, 'CreateProductCount', 'createProductCount', 0);
        const linkCount = pick(preview, 'LinkCount', 'linkCount', 0);
        if (isFxPriceBlocked(this.state.shopify)) {
            this.setState({ error: this.props.t('integrations.fxRateRequired') });
            return;
        }
        if (!createCount && !linkCount) {
            this.setState({ error: 'Nothing to import. Add SKUs in Shopify, or those products are already linked.' });
            return;
        }
        if (!window.confirm(`Import ${createCount} Shopify product${createCount === 1 ? '' : 's'} and link ${linkCount} existing SKU${linkCount === 1 ? '' : 's'} into Bistro? Variants without a SKU are skipped.`)) {
            return;
        }
        this.setState({ saving: true, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/catalog/import', {
                CategoryId: this.state.importCategoryId ? Number(this.state.importCategoryId) : 0,
                LocationId: this.state.locationId || undefined,
                ImportScope: this.state.importScope === 'Location' ? 'Location' : 'All',
            });
            if (res.data.status !== 'Ok') {
                this.setState({ saving: false, error: res.data.message || 'Unable to import the Shopify catalog.' });
                return;
            }
            const result = (res.data && res.data.result) || {};
            this.setState({
                saving: false,
                notice: pick(result, 'Message', 'message', '') || 'Catalog imported.',
                shopify: res.data.shopify ? normalizeShopify(res.data.shopify) : this.state.shopify,
            });
            await this.loadStatus();
            this.loadMatch();
            this.loadImportPreview();
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Unable to import the Shopify catalog.' });
        }
    };

    setCatalogSync = async (enabled) => {
        this.setState({ saving: true, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/catalog/enable', { Enabled: enabled });
            if (res.data.status !== 'Ok') {
                this.setState({ saving: false, error: res.data.message || 'Unable to update catalog sync.' });
                return;
            }
            this.setState({
                saving: false,
                shopify: normalizeShopify(res.data.shopify || this.state.shopify),
                notice: enabled
                    ? 'Catalog auto-sync is on. Name, price, and image changes in Bistro will push to the online store.'
                    : 'Catalog auto-sync is off. You can still copy name, price, and image with the buttons below.',
            });
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Unable to update catalog sync.' });
        }
    };

    pullCatalog = async () => {
        if (isFxPriceBlocked(this.state.shopify)) {
            this.setState({ error: this.props.t('integrations.fxRateRequired') });
            return;
        }
        const preview = await this.loadImportPreview() || this.state.importPreview || {};
        const createCount = pick(preview, 'CreateProductCount', 'createProductCount', 0);
        const linkCount = pick(preview, 'LinkCount', 'linkCount', 0);
        if ((createCount || linkCount)
            && !window.confirm(`This will import ${createCount} Shopify product${createCount === 1 ? '' : 's'} and link ${linkCount} existing SKU${linkCount === 1 ? '' : 's'} into Bistro, then copy name, price, and image for linked items. Continue?`)) {
            return;
        }
        this.setState({ saving: true, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/catalog/pull', {
                LocationId: this.state.locationId || undefined,
            });
            const result = (res.data && res.data.result) || {};
            const created = pick(result, 'CreatedCount', 'createdCount', 0);
            const updated = pick(result, 'UpdatedCount', 'updatedCount', 0);
            const apiMessage = pick(result, 'Message', 'message', '');
            this.setState({
                saving: false,
                error: res.data.status === 'Ok' ? '' : (res.data.message || 'Catalog pull failed.'),
                notice: res.data.status === 'Ok'
                    ? (apiMessage || (created
                        ? `Imported ${created} Shopify product(s) into Bistro and updated ${updated} linked items.`
                        : `Copied name, price, and image from the online store on ${updated} linked items.`))
                    : '',
                shopify: res.data.shopify ? normalizeShopify(res.data.shopify) : this.state.shopify,
            });
            if (res.data.status === 'Ok') {
                this.loadMatch();
                this.loadImportPreview();
            }
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Catalog pull failed.' });
        }
    };

    pushCatalog = async () => {
        if (isFxPriceBlocked(this.state.shopify)) {
            this.setState({ error: this.props.t('integrations.fxRateRequired') });
            return;
        }
        this.setState({ saving: true, error: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/catalog/push', {
                LocationId: this.state.locationId || undefined,
            });
            const result = (res.data && res.data.result) || {};
            const created = pick(result, 'CreatedCount', 'createdCount', 0);
            const updated = pick(result, 'UpdatedCount', 'updatedCount', 0);
            const apiMessage = pick(result, 'Message', 'message', '');
            this.setState({
                saving: false,
                error: res.data.status === 'Ok' ? '' : (res.data.message || 'Catalog push failed.'),
                notice: res.data.status === 'Ok'
                    ? (apiMessage || (created
                        ? `Created ${created} Shopify product(s) and updated ${updated} linked items.`
                        : `Copied name, price, and image to the online store on ${updated} linked items.`))
                    : '',
                shopify: res.data.shopify ? normalizeShopify(res.data.shopify) : this.state.shopify,
            });
            if (res.data.status === 'Ok') {
                this.loadMatch();
                this.loadImportPreview();
            }
        } catch (err) {
            this.setState({ saving: false, error: err.message || 'Catalog push failed.' });
        }
    };

    linkUnmatched = async (row) => {
        const key = rowKey(row);
        const inventoryItemId = this.state.linkByKey[key];
        if (!inventoryItemId) {
            this.setState({ error: 'Select a Shopify variant to link.' });
            return;
        }
        this.setState({ linkingKey: key, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/link', {
                EntityType: pick(row, 'EntityType', 'entityType', ''),
                EntityId: pick(row, 'EntityId', 'entityId', ''),
                ShopifyInventoryItemId: inventoryItemId,
                LocationId: this.state.locationId || undefined,
            });
            if (res.data.status !== 'Ok') {
                this.setState({ linkingKey: '', error: res.data.message || 'Unable to link that item.' });
                return;
            }
            const nextLink = { ...this.state.linkByKey };
            delete nextLink[key];
            this.setState({
                linkingKey: '',
                preview: res.data.preview || this.state.preview,
                shopify: res.data.shopify ? normalizeShopify(res.data.shopify) : this.state.shopify,
                linkByKey: nextLink,
                notice: 'Linked. Quantities are not copied until you pull, push, or resolve a conflict.',
            });
        } catch (err) {
            this.setState({ linkingKey: '', error: err.message || 'Unable to link that item.' });
        }
    };

    resolveConflict = async (conflict, resolution) => {
        const conflictId = pick(conflict, 'ConflictId', 'conflictId', 0);
        this.setState({ resolvingId: conflictId, error: '', notice: '' });
        try {
            const res = await axios.post('/api/integrations/shopify/conflicts/resolve', {
                ConflictId: conflictId,
                Resolution: resolution,
            });
            if (res.data.status !== 'Ok') {
                this.setState({ resolvingId: null, error: res.data.message || 'Unable to resolve the conflict.' });
                return;
            }
            this.setState({
                resolvingId: null,
                conflicts: res.data.conflicts || [],
                shopify: res.data.shopify ? normalizeShopify(res.data.shopify) : this.state.shopify,
                notice: resolution === 'Dismiss' ? 'Conflict dismissed.' : 'Conflict resolved and inventory updated.',
            });
            await this.loadMatch();
        } catch (err) {
            this.setState({ resolvingId: null, error: err.message || 'Unable to resolve the conflict.' });
        }
    };

    renderCurrencyFx(shopify, t) {
        if (!shopify || !shopify.Connected) return null;
        const { fxRateDraft, savingFxRate, saving } = this.state;
        const shopCode = (shopify.ShopifyCurrencyCode || '').toUpperCase();
        const accountCode = (shopify.AccountCurrencyCode || '').toUpperCase()
            || t('integrations.currencyUnknown');
        const blocked = isFxPriceBlocked(shopify);
        const shopUnknown = !shopify.ShopifyCurrencyCode;
        return (
            <div className={s.fillBlock}>
                <h4>{t('integrations.currencyTitle')}</h4>
                <p className={s.noteText}>
                    {t('integrations.currencySummary', {
                        shop: shopCode || t('integrations.currencyUnknown'),
                        account: accountCode,
                    })}
                </p>
                {shopify.FxNote ? <p className={s.noteText}>{shopify.FxNote}</p> : null}
                {shopUnknown ? (
                    <Alert color="warning">{t('integrations.fxCurrencyUnknown')}</Alert>
                ) : null}
                {shopify.FxRequired ? (
                    <FormGroup>
                        <label htmlFor="catalogFxRate">{t('integrations.fxRate')}</label>
                        <div className={s.redirectRow}>
                            <Input
                                id="catalogFxRate"
                                type="number"
                                min="0"
                                step="any"
                                value={fxRateDraft}
                                placeholder={t('integrations.fxRatePlaceholder')}
                                disabled={saving || savingFxRate}
                                onChange={(e) => this.setState({ fxRateDraft: e.target.value })}
                            />
                            <Button
                                type="button"
                                color="secondary"
                                outline
                                disabled={saving || savingFxRate}
                                onClick={this.saveFxRate}
                            >
                                {t('integrations.fxRateSave')}
                            </Button>
                        </div>
                        <p className={s.noteText}>
                            {t('integrations.fxRateHelp', { shop: shopCode || 'SHOP', account: accountCode })}
                        </p>
                        {blocked ? <Alert color="warning">{t('integrations.fxRateRequired')}</Alert> : null}
                    </FormGroup>
                ) : (
                    !shopUnknown ? <p className={s.noteText}>{t('integrations.fxNotRequired')}</p> : null
                )}
            </div>
        );
    }

    renderCatalogImport(preview, importCategoryId, saving, t, importPreviewError) {
        const { locations, locationId, importScope, shopify } = this.state;
        const createCount = pick(preview, 'CreateProductCount', 'createProductCount', 0);
        const variationCount = pick(preview, 'CreateVariationCount', 'createVariationCount', 0);
        const linkCount = pick(preview, 'LinkCount', 'linkCount', 0);
        const skipCount = pick(preview, 'SkipCount', 'skipCount', 0);
        const shopifyCount = pick(preview, 'ShopifyProductCount', 'shopifyProductCount', 0);
        const categories = (preview && (preview.Categories || preview.categories)) || [];
        const rows = (preview && (preview.Rows || preview.rows)) || [];
        const fxBlocked = isFxPriceBlocked(shopify);
        const scopeLabel = importScope === 'Location'
            ? (pick(preview, 'LocationName', 'locationName', '')
                || ((locations || []).find((loc) => String(loc.Id) === String(locationId)) || {}).Name
                || locationId
                || t('integrations.importScopeLocation'))
            : t('integrations.importScopeAll');
        return (
            <div className={s.fillBlock}>
                <h4>{t('integrations.importCatalog')}</h4>
                <p className={s.noteText}>{t('integrations.importHelp')}</p>
                <FormGroup>
                    <label htmlFor="importScope">{t('integrations.importScope')}</label>
                    <Input
                        id="importScope"
                        type="select"
                        value={importScope === 'Location' ? `Location:${locationId || ''}` : 'All'}
                        disabled={saving}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'All') {
                                this.setState({ importScope: 'All' }, () => this.loadImportPreview());
                                return;
                            }
                            const nextLocationId = value.indexOf('Location:') === 0
                                ? value.slice('Location:'.length)
                                : value;
                            this.setState(
                                { importScope: 'Location', locationId: nextLocationId },
                                () => this.loadImportPreview(),
                            );
                        }}
                    >
                        <option value="All">{t('integrations.importScopeAll')}</option>
                        {(locations || []).map((loc) => {
                            const id = loc.Id || loc.id || '';
                            const name = loc.Name || loc.name || id;
                            return (
                                <option key={id} value={`Location:${id}`}>
                                    {name}
                                </option>
                            );
                        })}
                    </Input>
                    <p className={s.noteText}>{t('integrations.importScopeHelp')}</p>
                </FormGroup>
                {importPreviewError ? <Alert color="warning">{importPreviewError}</Alert> : null}
                {preview ? (
                <p className={s.noteText}>
                    {t('integrations.importSummary', {
                        shopify: shopifyCount,
                        create: createCount,
                        variations: variationCount,
                        link: linkCount,
                        skip: skipCount,
                    })}
                    {' · '}
                    {t('integrations.importScopeSummary', { scope: scopeLabel })}
                </p>
                ) : (
                    <p className={s.noteText}>{importPreviewError ? '' : 'Loading Shopify products…'}</p>
                )}
                {categories.length > 0 ? (
                    <FormGroup>
                        <label htmlFor="importCategory">{t('integrations.importCategory')}</label>
                        <Input
                            id="importCategory"
                            type="select"
                            value={importCategoryId}
                            disabled={saving}
                            onChange={(e) => this.setState({ importCategoryId: e.target.value })}
                        >
                            <option value="">{t('integrations.importCategoryDefault')}</option>
                            {categories.map((cat) => {
                                const id = pick(cat, 'CategoryId', 'categoryId', 0);
                                return (
                                    <option key={id} value={id}>{pick(cat, 'Name', 'name', '')}</option>
                                );
                            })}
                        </Input>
                    </FormGroup>
                ) : (
                    <p className={s.noteText}>{t('integrations.importCategoryCreate')}</p>
                )}
                {rows.length > 0 ? (
                    <div className={s.tableWrap}>
                        <Table size="sm" className={s.table} responsive>
                            <thead>
                                <tr>
                                    <th>Shopify</th>
                                    <th>Action</th>
                                    <th>SKUs</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.slice(0, 50).map((row) => {
                                    const action = pick(row, 'Action', 'action', '');
                                    const skus = pick(row, 'Skus', 'skus', []) || [];
                                    return (
                                        <tr key={pick(row, 'ShopifyProductId', 'shopifyProductId', pick(row, 'ShopifyTitle', 'shopifyTitle', ''))}>
                                            <td>{pick(row, 'ShopifyTitle', 'shopifyTitle', '')}</td>
                                            <td>
                                                {action === 'Skip'
                                                    ? (pick(row, 'SkipReason', 'skipReason', 'Skip') )
                                                    : (action === 'Link'
                                                        ? t('integrations.importLink', { count: pick(row, 'ImportableCount', 'importableCount', skus.length) })
                                                        : (action === 'CreateSimple'
                                                            ? t('integrations.importSimple')
                                                            : t('integrations.importVariations', { count: pick(row, 'ImportableCount', 'importableCount', skus.length) })))}
                                            </td>
                                            <td>{skus.length ? skus.join(', ') : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        {rows.length > 50 ? <p className={s.noteText}>Showing 50 of {rows.length}.</p> : null}
                    </div>
                ) : null}
                {importPreviewError ? (
                    <Button color="secondary" className={s.actionBtn} disabled={saving} onClick={this.loadImportPreview}>
                        Retry import preview
                    </Button>
                ) : (
                    <Button
                        color="primary"
                        className={s.actionBtn}
                        disabled={saving || fxBlocked || (!createCount && !linkCount)}
                        onClick={this.importCatalog}
                    >
                        {t('integrations.importNow')}
                    </Button>
                )}
            </div>
        );
    }

    renderMatchTable(rows, emptyLabel) {
        if (!rows || rows.length === 0) {
            return <p className={s.noteText}>{emptyLabel}</p>;
        }
        return (
            <div className={s.tableWrap}>
                <Table size="sm" className={s.table} responsive>
                    <thead>
                        <tr>
                            <th>Bistro</th>
                            <th>SKU</th>
                            <th>Bistro qty</th>
                            <th>Online store</th>
                            <th>Online qty</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0, 50).map((row) => (
                            <tr key={`${row.EntityType || row.entityType}-${row.EntityId || row.entityId}`}>
                                <td>{row.BistroName || row.bistroName}</td>
                                <td>{row.MatchSku || row.matchSku || row.BistroSku || row.bistroSku || '—'}</td>
                                <td>{row.BistroQuantity != null ? row.BistroQuantity : row.bistroQuantity}</td>
                                <td>{row.ShopifyTitle || row.shopifyTitle || row.UnmatchedReason || row.unmatchedReason || '—'}</td>
                                <td>{row.ShopifyAvailable == null && row.shopifyAvailable == null ? '—' : (row.ShopifyAvailable != null ? row.ShopifyAvailable : row.shopifyAvailable)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {rows.length > 50 ? <p className={s.noteText}>Showing 50 of {rows.length}.</p> : null}
            </div>
        );
    }

    renderUnmatchedSection(rows, unlinked, open, t) {
        const count = (rows && rows.length) || 0;
        return (
            <div className={s.collapseBlock}>
                <button
                    type="button"
                    className={s.collapseToggle}
                    aria-expanded={open}
                    onClick={() => this.setState({ unmatchedOpen: !open })}
                >
                    <i className={`zmdi ${open ? 'zmdi-chevron-down' : 'zmdi-chevron-right'}`} aria-hidden="true" />
                    <span className={s.collapseLabel}>{t('integrations.unmatched')} ({count})</span>
                </button>
                {open ? (
                    <div>
                        <p className={s.noteText}>
                            {t('integrations.linkHelp')}
                        </p>
                        {this.renderUnmatchedTable(rows, unlinked)}
                    </div>
                ) : (
                    count > 0 ? <p className={s.noteText}>{t('integrations.unmatchedCollapsed', { count })}</p> : null
                )}
            </div>
        );
    }

    renderUnmatchedTable(rows, unlinked) {
        if (!rows || rows.length === 0) {
            return <p className={s.noteText}>Every sync-eligible item matched.</p>;
        }
        const options = unlinked || [];
        const { linkByKey, linkingKey, saving } = this.state;
        return (
            <div className={s.tableWrap}>
                <Table size="sm" className={s.table} responsive>
                    <thead>
                        <tr>
                            <th>Bistro</th>
                            <th>SKU</th>
                            <th>Bistro qty</th>
                            <th>Reason</th>
                            <th>Link to Shopify variant</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0, 50).map((row) => {
                            const key = rowKey(row);
                            const selected = linkByKey[key] || '';
                            return (
                                <tr key={key}>
                                    <td>{pick(row, 'BistroName', 'bistroName', '')}</td>
                                    <td>{pick(row, 'MatchSku', 'matchSku', '') || pick(row, 'BistroSku', 'bistroSku', '') || '—'}</td>
                                    <td>{pick(row, 'BistroQuantity', 'bistroQuantity', '—')}</td>
                                    <td>{pick(row, 'UnmatchedReason', 'unmatchedReason', '—')}</td>
                                    <td>
                                        <div className={s.linkRow}>
                                            <Input
                                                type="select"
                                                value={selected}
                                                disabled={saving || linkingKey === key || options.length === 0}
                                                onChange={(e) => this.setState({
                                                    linkByKey: { ...this.state.linkByKey, [key]: e.target.value },
                                                })}
                                            >
                                                <option value="">
                                                    {options.length === 0 ? 'No unlinked Shopify variants' : 'Select variant'}
                                                </option>
                                                {options.map((variant) => {
                                                    const id = pick(variant, 'ShopifyInventoryItemId', 'shopifyInventoryItemId', '');
                                                    return (
                                                        <option key={id} value={id}>{variantOptionLabel(variant)}</option>
                                                    );
                                                })}
                                            </Input>
                                            <Button
                                                type="button"
                                                color="secondary"
                                                size="sm"
                                                disabled={saving || linkingKey === key || !selected}
                                                onClick={() => this.linkUnmatched(row)}
                                            >
                                                {linkingKey === key ? 'Linking…' : 'Link'}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                {rows.length > 50 ? <p className={s.noteText}>Showing 50 of {rows.length}.</p> : null}
            </div>
        );
    }

    renderConflicts(conflicts) {
        if (!conflicts || conflicts.length === 0) {
            return <p className={s.noteText}>No open inventory conflicts.</p>;
        }
        const { resolvingId, saving } = this.state;
        return (
            <div className={s.tableWrap}>
                <Table size="sm" className={s.table} responsive>
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>SKU</th>
                            <th>Bistro qty</th>
                            <th>Online qty</th>
                            <th>Last known</th>
                            <th>Detected</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {conflicts.map((conflict) => {
                            const id = pick(conflict, 'ConflictId', 'conflictId', 0);
                            const busy = resolvingId === id || saving;
                            const shopifyQty = pick(conflict, 'ShopifyQuantity', 'shopifyQuantity', null);
                            return (
                                <tr key={id}>
                                    <td>
                                        {pick(conflict, 'BistroName', 'bistroName', '')}
                                        {pick(conflict, 'Note', 'note', '') ? (
                                            <div className={s.noteText}>{pick(conflict, 'Note', 'note', '')}</div>
                                        ) : null}
                                    </td>
                                    <td>{pick(conflict, 'SKU', 'sku', '') || '—'}</td>
                                    <td>{pick(conflict, 'BistroQuantity', 'bistroQuantity', '—')}</td>
                                    <td>{shopifyQty == null ? '—' : shopifyQty}</td>
                                    <td>{pick(conflict, 'LastKnownQuantity', 'lastKnownQuantity', null) == null ? '—' : pick(conflict, 'LastKnownQuantity', 'lastKnownQuantity', '')}</td>
                                    <td>{formatApiDateTime(pick(conflict, 'DetectedAt', 'detectedAt', null))}</td>
                                    <td>
                                        <div className={s.conflictActions}>
                                            <Button type="button" color="primary" size="sm" disabled={busy} onClick={() => this.resolveConflict(conflict, 'Bistro')}>
                                                Take Bistro
                                            </Button>
                                            <Button type="button" color="secondary" size="sm" disabled={busy} onClick={() => this.resolveConflict(conflict, 'Shopify')}>
                                                Take online
                                            </Button>
                                            <Button type="button" color="link" size="sm" disabled={busy} onClick={() => this.resolveConflict(conflict, 'Dismiss')}>
                                                Dismiss
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        );
    }

    renderShopifyHelp(helpOpen, helpPos, t) {
        if (!helpOpen) {
            return null;
        }
        const pos = helpPos || { x: 72, y: 72 };
        return (
            <div
                className={s.helpPanel}
                style={{ left: pos.x, top: pos.y }}
                role="dialog"
                aria-labelledby="shopify-help-title"
            >
                <div
                    className={s.helpPanelHeader}
                    onMouseDown={this.startHelpDrag}
                    onTouchStart={this.startHelpDrag}
                >
                    <h3 id="shopify-help-title">{t('integrations.helpTitle')}</h3>
                    <button
                        type="button"
                        className={s.helpPanelClose}
                        onClick={this.closeShopifyHelp}
                        aria-label={t('integrations.helpClose')}
                    >
                        ×
                    </button>
                </div>
                <div className={s.helpPanelBody}>
                    <h4>{t('integrations.helpConnectTitle')}</h4>
                    <ol>
                        <li>{t('integrations.helpConnect1')}</li>
                        <li>{t('integrations.helpConnect2')}</li>
                        <li>{t('integrations.helpConnect3')}</li>
                        <li>{t('integrations.helpConnect4')}</li>
                        <li>{t('integrations.helpConnect5')}</li>
                        <li>{t('integrations.helpConnect6')}</li>
                    </ol>
                    <h4>{t('integrations.helpImportTitle')}</h4>
                    <ol>
                        <li>{t('integrations.helpImport1')}</li>
                        <li>{t('integrations.helpImport2')}</li>
                        <li>{t('integrations.helpImport3')}</li>
                        <li>{t('integrations.helpImport4')}</li>
                    </ol>
                    <h4>{t('integrations.helpTipsTitle')}</h4>
                    <ul>
                        <li>{t('integrations.helpTip1')}</li>
                        <li>{t('integrations.helpTip2')}</li>
                        <li>{t('integrations.helpTip3')}</li>
                        <li>{t('integrations.helpTip4')}</li>
                    </ul>
                </div>
            </div>
        );
    }

    renderCredentialFields() {
        const { shopify, shopDomain, clientId, clientSecret, saving } = this.state;
        const { t } = this.props;
        const redirectUri = shopifyOAuthRedirectUri(shopify);
        const secretPlaceholder = shopify && shopify.HasClientSecret
            ? 'Saved — enter a new secret to replace it'
            : 'Shopify Client secret';

        return (
            <div>
                <p className={s.noteText}>
                    {t('integrations.helpHint')}
                </p>
                <FormGroup>
                    <label htmlFor="shopifyRedirect">Allowed redirection URL</label>
                    <div className={s.redirectRow}>
                        <Input id="shopifyRedirect" value={redirectUri} readOnly />
                        <Button type="button" color="secondary" outline disabled={saving || !redirectUri} onClick={this.copyRedirect}>Copy</Button>
                    </div>
                </FormGroup>
                <FormGroup>
                    <label htmlFor="shopifyClientId">Client ID</label>
                    <Input
                        id="shopifyClientId"
                        autoComplete="off"
                        value={clientId}
                        placeholder="Shopify Client ID"
                        onChange={(e) => this.setState({ clientId: e.target.value })}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="shopifyClientSecret">Client secret</label>
                    <Input
                        id="shopifyClientSecret"
                        type="password"
                        autoComplete="new-password"
                        value={clientSecret}
                        placeholder={secretPlaceholder}
                        onChange={(e) => this.setState({ clientSecret: e.target.value })}
                    />
                </FormGroup>
                <FormGroup>
                    <label htmlFor="shopDomain">Shopify store domain</label>
                    <Input
                        id="shopDomain"
                        value={shopDomain}
                        placeholder="your-store.myshopify.com"
                        onChange={(e) => this.setState({ shopDomain: e.target.value })}
                    />
                </FormGroup>
            </div>
        );
    }

    render() {
        const {
            loading, saving, error, notice, shopify, locations, locationId, preview, importPreview, importPreviewError, importCategoryId, unmatchedOpen, fillDirection, panelOpen, helpOpen, helpPos, conflicts,
            check, checkLoading, checkSaving, checkError, checkNotice, checkPanelOpen, checkEmployees, checkEmployeeBusyId,
            checkNameUser, checkLegalFirst, checkLegalLast,
        } = this.state;
        const { t } = this.props;
        const connected = !!(shopify && shopify.Connected);
        const syncEnabled = !!(shopify && shopify.SyncEnabled);
        const checkConnected = !!(check && check.Connected);
        const checkEnabled = !!(check && check.PayrollEnabled);

        return (
            <Widget title={t('settingsTabs.integrations')} className={s.integrationsWidget}>
                {loading ? <Spinner size="sm" /> : null}
                {!panelOpen && error ? <Alert color="danger">{error}</Alert> : null}
                {!panelOpen && notice ? <Alert color="success">{notice}</Alert> : null}

                <div className={s.card}>
                    <div className={s.cardHeader}>
                        <a
                            className={s.brandLink}
                            href={SHOPIFY_HOME}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img className={s.brandLogo} src={SHOPIFY_BAG_SRC} alt="Shopify" />
                        </a>
                        <div>
                            <h3>{t('integrations.shopify')}</h3>
                            <p className={s.noteText}>
                                {t('integrations.shopifyIntro')}
                            </p>
                        </div>
                    </div>

                    <div className={s.launchRow}>
                        <Button color="primary" className={s.launchBtn} disabled={loading} onClick={this.openPanel}>
                            <img className={s.launchLogo} src={SHOPIFY_BAG_SRC} alt="" />
                            {t('integrations.shopifyIntegration')}
                        </Button>
                        {connected ? (
                            <span className={s.statusChip}>
                                {t('integrations.connectedDomain', { domain: shopify.ShopDomain })}
                                {syncEnabled ? ` · ${t('integrations.syncOn')}` : ` · ${t('integrations.syncPaused')}`}
                            </span>
                        ) : (
                            <span className={s.statusChip}>{t('common.notConnected')}</span>
                        )}
                    </div>

                    {connected ? (
                        <div>
                            <p className={s.statusLine}>
                                Connected: <strong>{shopify.ShopDomain}</strong>
                                {shopify.LocationName ? ` · Location: ${shopify.LocationName}` : ''}
                                {` · ${syncEnabled ? 'Sync on' : 'Sync paused'}`}
                                {` · Mapped SKUs: ${shopify.MappedCount || 0}`}
                                {shopify.LastReconcileAt ? ` · Last reconcile: ${formatApiDateTime(shopify.LastReconcileAt)}` : ''}
                                {` · Open conflicts: ${shopify.OpenConflictCount || 0}`}
                                {` · Failed sync jobs: ${shopify.FailedOutboxCount || 0}`}
                                {shopify.LastCatalogSyncAt ? ` · Last catalog sync: ${formatApiDateTime(shopify.LastCatalogSyncAt)}` : ''}
                            </p>
                            {shopify.LastError ? <Alert color="warning">{shopify.LastError}</Alert> : null}
                            {(shopify.OpenConflictCount || 0) > 0 ? (
                                <Alert color="warning">
                                    Inventory quantities differ on {shopify.OpenConflictCount} linked item{(shopify.OpenConflictCount === 1) ? '' : 's'}. Choose which quantity to keep below.
                                </Alert>
                            ) : null}

                            {locations.length > 1 || !shopify.ShopifyLocationId ? (
                                <FormGroup>
                                    <label htmlFor="shopLocation">{t('integrations.shopifyLocation')}</label>
                                    <Input
                                        id="shopLocation"
                                        type="select"
                                        value={locationId}
                                        onChange={(e) => this.setState({ locationId: e.target.value }, () => {
                                            this.loadMatch();
                                            this.loadImportPreview();
                                        })}
                                    >
                                        <option value="">{t('integrations.selectLocation')}</option>
                                        {locations.map((loc) => (
                                            <option key={loc.Id} value={loc.Id}>{loc.Name}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            ) : null}

                            {this.renderCurrencyFx(shopify, t)}

                            {this.renderCatalogImport(importPreview, importCategoryId, saving, t, importPreviewError)}

                            {preview ? (
                                <div className={s.matchBlock}>
                                    <h4>{t('integrations.skuMatch')}</h4>
                                    <p className={s.noteText}>
                                        Matched { (preview.Matches || preview.matches || []).length }
                                        {` · Unmatched ${(preview.Unmatched || preview.unmatched || []).length}`}
                                        {` · Internal only ${(preview.Excluded || preview.excluded || []).length}`}
                                    </p>
                                    <h5>{t('integrations.matched')}</h5>
                                    {this.renderMatchTable(preview.Matches || preview.matches, 'No matching SKUs yet.')}
                                    {this.renderUnmatchedSection(
                                        preview.Unmatched || preview.unmatched,
                                        preview.UnlinkedShopify || preview.unlinkedShopify,
                                        unmatchedOpen,
                                        t,
                                    )}
                                    <h5>{t('integrations.excluded')}</h5>
                                    {this.renderMatchTable(preview.Excluded || preview.excluded, 'No internal-only items with SKUs.')}
                                </div>
                            ) : null}

                            <div className={s.matchBlock}>
                                <h4>{t('integrations.conflicts')}</h4>
                                {this.renderConflicts(conflicts)}
                            </div>

                            {!syncEnabled ? (
                                <div className={s.fillBlock}>
                                    <h4>{t('integrations.initialFill')}</h4>
                                    <p className={s.noteText}>{t('integrations.fillHelp')}</p>
                                    <FormGroup check>
                                        <CustomInput
                                            type="radio"
                                            id="fillShopifyToBistro"
                                            name="fillDirection"
                                            label={t('integrations.fillOnlineToBistro')}
                                            checked={fillDirection === FILL_SHOPIFY_TO_BISTRO}
                                            onChange={() => this.setState({ fillDirection: FILL_SHOPIFY_TO_BISTRO })}
                                        />
                                    </FormGroup>
                                    <FormGroup check>
                                        <CustomInput
                                            type="radio"
                                            id="fillBistroToShopify"
                                            name="fillDirection"
                                            label={t('integrations.fillBistroToOnline')}
                                            checked={fillDirection === FILL_BISTRO_TO_SHOPIFY}
                                            onChange={() => this.setState({ fillDirection: FILL_BISTRO_TO_SHOPIFY })}
                                        />
                                    </FormGroup>
                                    <FormGroup check>
                                        <CustomInput
                                            type="radio"
                                            id="fillMatchOnly"
                                            name="fillDirection"
                                            label={t('integrations.fillMatchOnly')}
                                            checked={fillDirection === FILL_MATCH_ONLY}
                                            onChange={() => this.setState({ fillDirection: FILL_MATCH_ONLY })}
                                        />
                                    </FormGroup>
                                    <Button
                                        color="primary"
                                        className={s.actionBtn}
                                        disabled={saving || ((locations.length > 1 || locations.length === 0) && !locationId)}
                                        onClick={this.enable}
                                    >
                                        {t('integrations.enableSync')}
                                    </Button>
                                </div>
                            ) : (
                                <div className={s.actions}>
                                    <Button color="secondary" disabled={saving} onClick={this.pull}>{t('integrations.copyOnlineToBistro')}</Button>
                                    <Button color="secondary" disabled={saving} onClick={this.push}>{t('integrations.copyBistroToOnline')}</Button>
                                    <Button color="warning" disabled={saving} onClick={this.disable}>{t('integrations.pauseSync')}</Button>
                                </div>
                            )}

                            <div className={s.fillBlock}>
                                <h4>{t('integrations.catalog')}</h4>
                                <p className={s.noteText}>
                                    {t('integrations.catalogHelp')}
                                </p>
                                {!shopify.CatalogWriteGranted ? (
                                    <Alert color="warning">
                                        Reconnect Shopify after adding write_products on the custom app to push catalog changes. Pull still works.
                                    </Alert>
                                ) : null}
                                <FormGroup check>
                                    <CustomInput
                                        type="checkbox"
                                        id="catalogSyncEnabled"
                                        label="Automatically push Bistro name, price, and image edits to the online store"
                                        checked={!!shopify.CatalogSyncEnabled}
                                        disabled={saving || !shopify.CatalogWriteGranted}
                                        onChange={(e) => this.setCatalogSync(e.target.checked)}
                                    />
                                </FormGroup>
                                <div className={s.actions}>
                                    <Button
                                        color="secondary"
                                        disabled={saving || isFxPriceBlocked(shopify)}
                                        onClick={this.pullCatalog}
                                    >
                                        Copy catalog online store → Bistro
                                    </Button>
                                    <Button
                                        color="secondary"
                                        disabled={saving || !shopify.CatalogWriteGranted || isFxPriceBlocked(shopify)}
                                        onClick={this.pushCatalog}
                                    >
                                        Copy catalog Bistro → online store
                                    </Button>
                                </div>
                                {shopify.CatalogSyncEnabled ? (
                                    <p className={s.noteText}>Auto-push is on for linked items.</p>
                                ) : null}
                            </div>

                            <Button color="link" className={s.disconnect} disabled={saving} onClick={this.disconnect}>
                                {t('integrations.disconnectShopify')}
                            </Button>
                        </div>
                    ) : null}
                </div>

                <div className={`${s.card} ${s.cardSpacer}`}>
                    <div className={s.cardHeader}>
                        <a
                            className={s.brandLink}
                            href={CHECK_HOME}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img className={s.brandLogo} src={CHECK_LOGO_SRC} alt="Check" />
                        </a>
                        <div>
                            <h3>{t('integrations.check')}</h3>
                            <p className={s.noteText}>
                                {t('integrations.checkIntro')}
                            </p>
                        </div>
                    </div>

                    {checkLoading ? <Spinner size="sm" /> : null}
                    {!checkPanelOpen && checkError ? <Alert color="danger">{checkError}</Alert> : null}
                    {!checkPanelOpen && checkNotice ? <Alert color="success">{checkNotice}</Alert> : null}

                    <div className={s.launchRow}>
                        <Button color="primary" className={s.launchBtn} disabled={checkLoading || checkSaving} onClick={this.openCheckPanel}>
                            <img className={s.launchLogo} src={CHECK_LOGO_SRC} alt="" />
                            {checkConnected ? t('integrations.checkIntegration') : t('integrations.connectCheck')}
                        </Button>
                        {check && !check.AppConfigured ? (
                            <span className={s.statusChip}>Not configured on this server</span>
                        ) : checkConnected ? (
                            <span className={s.statusChip}>
                                Connected
                                {check.TradeName || check.LegalName ? ` · ${check.TradeName || check.LegalName}` : ''}
                                {` · ${checkOnboardLabel(check.OnboardStatus)}`}
                                {checkEnabled ? ' · Payroll on' : ' · Payroll paused'}
                            </span>
                        ) : (
                            <span className={s.statusChip}>Not connected</span>
                        )}
                    </div>

                    {checkConnected ? (
                        <div>
                            <p className={s.statusLine}>
                                Company: <strong>{check.LegalName || check.CompanyId}</strong>
                                {` · ${checkOnboardLabel(check.OnboardStatus)}`}
                                {` · ${checkEnabled ? 'Payroll on' : 'Payroll paused'}`}
                                {check.IsSandbox ? ' · Sandbox' : ''}
                                {check.InGoodStanding ? ' · In good standing' : ''}
                                {` · Employees mapped: ${check.MappedEmployeeCount || 0}`}
                            </p>
                            {check.LastError ? <Alert color="warning">{check.LastError}</Alert> : null}
                            {(check.BlockingSteps || []).length > 0 ? (
                                <Alert color="warning">
                                    Finish Check Onboard: {(check.BlockingSteps || []).join(', ')}.
                                </Alert>
                            ) : null}
                            {(check.RemainingSteps || []).length > 0 ? (
                                <Alert color="secondary">
                                    Remaining Onboard steps: {(check.RemainingSteps || []).join(', ')}.
                                </Alert>
                            ) : null}
                            {check.Connected && check.AppConfigured && !check.WebhookConfigured ? (
                                <Alert color="warning">
                                    Check webhook key is not set on the server. Onboard status still refreshes when you open this page; live events will not apply until Rebelity sets Check:WebhookKey.
                                </Alert>
                            ) : null}
                            <div className={s.actions}>
                                <Button color="secondary" disabled={checkSaving} onClick={this.continueCheckOnboard}>
                                    {checkSaving ? 'Opening…' : 'Open Check Onboard'}
                                </Button>
                                {!checkEnabled ? (
                                    <Button color="primary" disabled={checkSaving || !check.CanEnablePayroll} onClick={this.enableCheckPayroll}>
                                        Enable payroll
                                    </Button>
                                ) : (
                                    <Button color="warning" disabled={checkSaving} onClick={this.disableCheckPayroll}>
                                        Pause payroll
                                    </Button>
                                )}
                            </div>
                            <div className={s.matchBlock}>
                                <h4>{t('integrations.employees')}</h4>
                                <p className={s.noteText}>
                                    Create each person in Check with their legal first and last name, then finish Employee Onboard
                                    (SSN, withholdings, and bank). Check tax preview requires onboard status completed.
                                    Rebelity does not store SSN.
                                </p>
                                <div className={s.tableWrap}>
                                    <Table size="sm" bordered>
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Check employee</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(checkEmployees || []).length === 0 ? (
                                                <tr>
                                                    <td colSpan="3">{checkError ? 'Unable to load employees.' : 'No users found for this location.'}</td>
                                                </tr>
                                            ) : (checkEmployees || []).map((row) => {
                                                const id = pick(row, 'BistroUserId', 'bistroUserId', 0);
                                                const mapped = !!pick(row, 'Mapped', 'mapped', false);
                                                const onboardDone = !!pick(row, 'OnboardCompleted', 'onboardCompleted', false);
                                                const busy = checkEmployeeBusyId === id;
                                                return (
                                                    <tr key={id}>
                                                        <td>
                                                            {pick(row, 'DisplayName', 'displayName', '')}
                                                            {pick(row, 'ReceivesHourlyWage', 'receivesHourlyWage', false) ? ' · hourly' : ''}
                                                            {pick(row, 'Email', 'email', '') ? (
                                                                <div className={s.noteText}>{pick(row, 'Email', 'email', '')}</div>
                                                            ) : null}
                                                        </td>
                                                        <td>
                                                            {mapped
                                                                ? (pick(row, 'CheckEmployeeId', 'checkEmployeeId', '')
                                                                    + (onboardDone
                                                                        ? ' · Onboard complete'
                                                                        : (pick(row, 'OnboardStatus', 'onboardStatus', '')
                                                                            ? ` · ${checkOnboardLabel(pick(row, 'OnboardStatus', 'onboardStatus', ''))}`
                                                                            : ' · Onboard incomplete')))
                                                                : 'Not in Check'}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                color={mapped ? 'secondary' : 'primary'}
                                                                size="sm"
                                                                disabled={checkSaving || busy}
                                                                onClick={() => this.onboardCheckEmployee(row)}
                                                            >
                                                                {busy ? 'Opening…' : (mapped ? 'Open employee Onboard' : 'Create in Check')}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                            <Button color="link" className={s.disconnect} disabled={checkSaving} onClick={this.disconnectCheck}>
                                Disconnect Check
                            </Button>
                        </div>
                    ) : null}
                </div>

                <Modal isOpen={panelOpen} toggle={this.closePanel} className={s.connectModal}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            this.connect();
                        }}
                    >
                        <ModalHeader toggle={this.closePanel}>
                            <span className={s.modalTitle}>
                                <img className={s.modalLogo} src={SHOPIFY_LOGO_SRC} alt="Shopify" />
                                <span>Integration</span>
                                <button
                                    type="button"
                                    className={s.helpIconBtn}
                                    onClick={this.openShopifyHelp}
                                    aria-label={t('integrations.helpAria')}
                                    title={t('integrations.helpAria')}
                                >
                                    <i className="zmdi zmdi-help-outline" aria-hidden="true" />
                                </button>
                            </span>
                        </ModalHeader>
                        <ModalBody>
                            {error ? <Alert color="danger">{error}</Alert> : null}
                            {notice ? <Alert color="success">{notice}</Alert> : null}
                            {this.renderCredentialFields()}
                        </ModalBody>
                        <ModalFooter>
                            <Button type="button" color="secondary" outline disabled={saving} onClick={this.saveCredentials}>
                                Save credentials
                            </Button>
                            <Button type="submit" color="primary" disabled={!this.canConnect()}>
                                {saving ? t('integrations.connecting') : t('integrations.connectShopify')}
                            </Button>
                        </ModalFooter>
                    </form>
                </Modal>
                {this.renderShopifyHelp(helpOpen, helpPos, t)}

                <Modal isOpen={checkPanelOpen} toggle={this.closeCheckPanel} className={s.connectModal}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            this.connectCheck();
                        }}
                    >
                        <ModalHeader toggle={this.closeCheckPanel}>
                            <span className={s.modalTitle}>
                                <img className={s.modalLogo} src={CHECK_LOGO_SRC} alt="Check" />
                                <span>Payroll</span>
                            </span>
                        </ModalHeader>
                        <ModalBody>
                            {checkError ? <Alert color="danger">{checkError}</Alert> : null}
                            {checkNotice ? <Alert color="success">{checkNotice}</Alert> : null}
                            {check && !check.AppConfigured ? (
                                <Alert color="warning">
                                    Check is not configured on this server. Rebelity must set the platform Check API key before a location can connect.
                                </Alert>
                            ) : null}
                            {this.renderCheckFields()}
                        </ModalBody>
                        <ModalFooter>
                            <Button type="button" color="secondary" outline disabled={checkSaving} onClick={this.closeCheckPanel}>
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" disabled={!this.canConnectCheck()}>
                                {checkSaving ? 'Connecting…' : (checkConnected ? 'Continue Onboard' : 'Connect Check')}
                            </Button>
                        </ModalFooter>
                    </form>
                </Modal>
                <Modal isOpen={!!checkNameUser} toggle={() => this.setState({ checkNameUser: null })}>
                    <ModalHeader toggle={() => this.setState({ checkNameUser: null })}>
                        Legal name for Check
                    </ModalHeader>
                    <ModalBody>
                        <p className={s.noteText}>
                            Enter the name as it should appear on a W-2. This is not the POS username.
                        </p>
                        <FormGroup>
                            <label htmlFor="checkLegalFirst">Legal first name</label>
                            <Input
                                id="checkLegalFirst"
                                value={checkLegalFirst}
                                onChange={(e) => this.setState({ checkLegalFirst: e.target.value })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <label htmlFor="checkLegalLast">Legal last name</label>
                            <Input
                                id="checkLegalLast"
                                value={checkLegalLast}
                                onChange={(e) => this.setState({ checkLegalLast: e.target.value })}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" color="secondary" outline onClick={() => this.setState({ checkNameUser: null })}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            color="primary"
                            disabled={!(checkLegalFirst || '').trim() || !(checkLegalLast || '').trim()}
                            onClick={this.submitCheckEmployeeName}
                        >
                            Create in Check
                        </Button>
                    </ModalFooter>
                </Modal>
            </Widget>
        );
    }
}

export default withTranslation(IntegrationsSettings);
